import axios from "axios";
import { prime } from "@/lib/prefetch";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API_BASE = `${BACKEND_URL}/api`;

// Public GETs whose responses are safe to keep hot in the prefetch cache.
// Only touch idempotent, guest-facing endpoints — never anything scoped
// to an admin/session or that mutates state.
const CACHEABLE_GETS = new Set([
  "/public/treatments",
  "/public/crew",
  "/public/treatment-categories",
  "/public/settings",
]);

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 15000,
});

api.interceptors.request.use((config) => {
  if ((config.url || "").startsWith("/public/")) {
    try {
      const token = localStorage.getItem("yacht_ipad_session");
      const cabin = localStorage.getItem("yacht_cabin_id");
      if (token && cabin && localStorage.getItem("yacht_ipad_mode") === "1") {
        config.headers["X-IPad-Token"] = token;
        config.headers["X-SLX-Cabin"] = cabin;
      }
    } catch { /* Personal sessions continue through the HttpOnly cookie. */ }
  }
  return config;
});

// Dashboard authentication is carried only by the HttpOnly session cookie.

// Silence axios errors in the console (we still reject the promise so callers can catch).
// This stops CRA's dev overlay from tripping on network hiccups (offline, 401 poll, 404 heartbeat).
api.interceptors.response.use(
  (r) => {
    // Any successful iPad/guest response means the cabin is bookable again
    // — clear the locked overlay if it was up.
    const url = r.config?.url || "";
    if (url.includes("/public/ipad/") || url.includes("/public/ipad-login")) {
      try { window.dispatchEvent(new CustomEvent("cabin-unlocked")); } catch { /* ignore */ }
    }
    // Prime the prefetch cache with successful GETs of allow-listed
    // public endpoints so returning to /menu or /reservations paints
    // instantly from the last known payload while a fresh fetch
    // validates in the background.
    if ((r.config?.method || "get").toLowerCase() === "get" && CACHEABLE_GETS.has(url)) {
      try { prime(url, r.data); } catch { /* prefetch is best-effort */ }
    }
    return r;
  },
  (err) => {
    // 423 Locked — cabin occupancy_status flipped to crew / not_available.
    // Broadcast a global event so the top-level CabinLockedOverlay can
    // paint a polite full-screen lockout. Message comes straight from the
    // backend so ops can tweak copy without a frontend deploy.
    if (err?.response?.status === 423) {
      const raw = err.response?.data?.detail;
      const message = typeof raw === "string" ? raw : "Bookings for this cabin are unavailable at the moment.";
      try { window.dispatchEvent(new CustomEvent("cabin-locked", { detail: { message } })); } catch { /* ignore */ }
    }
    // Attach a friendly tag so consumers can still read the status.
    if (err && !err.__silenced) {
      err.__silenced = true;
      // eslint-disable-next-line no-console
      console.warn("[api]", err.config?.method?.toUpperCase(), err.config?.url, "→", err.response?.status, err.response?.data?.detail || err.message);
    }
    return Promise.reject(err);
  }
);

export function fileUrl(path) {
  if (!path) return "";
  return `${API_BASE}/files/${path}`;
}

export function formatErr(detail) {
  // Return an empty (falsy) string when the backend gave us no `detail` —
  // this lets callers use the `formatErr(...) || "friendly fallback"` pattern
  // to surface a *contextual* message on network hiccups instead of a generic
  // "Something went wrong." that used to short-circuit every fallback.
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}
